import { GuarantorsConsumerRenderProps, withGuarantorsContext } from 'components/guarantors/GuarantorsContext';
import { MessageSourceProps, withMessages } from 'react-message-source';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import React, { Fragment, useEffect, useState } from 'react';
import { CheckboxSc } from 'components/final-form/CheckBox';
import { Icon, Popup } from 'semantic-ui-react';
import moment from 'moment';
import { PatientEmailImportDto } from 'ts-types/api.types';
import styled from 'styled-components';

const ResultsPane = styled.div`
  flex: 0 0 auto;
  margin-right: 15px;
  min-width: 15rem;
  max-width: 36rem;

  .results-header {
    font-weight: bold;
    font-size: 1.1rem;
    border-bottom: 1px solid #e5e5e5;
    margin-bottom: 0.5rem;
  }

  .results-container {
    display: grid;
    grid-template-columns: minmax(min-content, 1fr);
    row-gap: 0.35rem;
    column-gap: 1.2rem;

    .import-error {
      color: #575757;
    }
  }
`;


interface Props extends MessageSourceProps, GuarantorsConsumerRenderProps {
  foundPatients: PatientEmailImportDto[],
  onPatientSelected: (patients: PatientEmailImportDto[]) => void;
}

const PatientEmailImportTableView = (props: Props) => {

  const {foundPatients, onPatientSelected, getMessage, getMessageWithNamedParams} = props;
  const [patients, setPatients] = useState([] as PatientEmailImportDto[]);

  useEffect(() => {

    setPatients(foundPatients);
  }, [foundPatients]);

  const patientsRowGetter = ({index}: any) => {

    Object.assign(patients[index], {index: index + 1});

    return patients[index];
  };

  const patientsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => selectPatient(index)}
        >
          {columns}
        </div>
    );
  };

  const selectHeaderColumn = () => {

    const countSelectablePatients = patients.filter(p => !p.invalid).length;
    const countSelectedPatients = patients.filter(p => p.selected).length;
    const indeterminate = countSelectedPatients > 0 && countSelectedPatients < countSelectablePatients;
    const noSelectedPatients = countSelectedPatients === 0 && countSelectablePatients > 0;
    const allPatientsSelected = countSelectablePatients > 0 && countSelectedPatients === countSelectablePatients;

    return (
        <CheckboxSc
            checked={countSelectedPatients > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedPatients || indeterminate) {
                  let modifiedPatients = [...patients];
                  const selectedPatients = modifiedPatients.map(p => {
                    if (!p.invalid) {
                      return {...p, selected: true};
                    }
                    return p;
                  });
                  onPatientSelected(selectedPatients);
                }

                if (allPatientsSelected) {
                  let modifiedPatients = [...patients];
                  const selectedPatients = modifiedPatients.map(p => ({...p, selected: false}));
                  onPatientSelected(selectedPatients);
                }

              }
            }
            disabled={countSelectablePatients === 0}
        />
    )
  };


  const selectPatient = (index: number) => {

    let patient = patients[index];

    if (patient && !patient.invalid) {
      let modifiedPatients = [...patients];
      modifiedPatients[index].selected = !modifiedPatients[index].selected;
      onPatientSelected(modifiedPatients);
    }
  }

  const patientSelectCellRenderer = (data: any) => {

    const {cellData, rowData} = data;

    const index = cellData - 1;
    const importErrors = rowData.importErrors;

    const trigger = (
        <Icon
            name="exclamation triangle"
            color="yellow"
        />
    );

    const content = (
        <ResultsPane>
          <div className="results-header">{getMessage("patientEmail.importError")}</div>
          <div className="results-container">
            {
                importErrors != null && importErrors.map((error: any) => {
                  let importErrorCode = "";

                  if (error === "NOT_EXISTING_PATIENTS_FOUND") {
                    let nonExistingPatients = "";

                    rowData.nonExistingPatientIds != null &&
                    rowData.nonExistingPatientIds.map((patientId: number, index: number) => {

                      nonExistingPatients = index > 0
                          ? nonExistingPatients + ", " + patientId.toString()
                          : nonExistingPatients + patientId.toString();

                    })

                    importErrorCode = getMessageWithNamedParams(`importErrorType.${error}`,
                        {notExistingPatients: nonExistingPatients})
                  } else {
                    importErrorCode = getMessage(`importErrorType.${error}`)
                  }
                  return (
                      <Fragment key={`error-row-${error}`}>
                        <div className="import-error">
                          {importErrorCode}
                        </div>
                      </Fragment>
                  );
                })
            }
          </div>
        </ResultsPane>
    );


    return (
        <div style={{display: "flex", "alignItems": "center"}}>
          <CheckboxSc
              checked={rowData.selected}
              onChange={() => selectPatient(index)}
          />
          {rowData.invalid &&
              <div>
                <Popup
                    trigger={trigger}
                    popperModifiers={{
                      preventOverflow: {
                        boundary: document.getElementById("patientEmailImportTable")
                      }
                    }}
                    content={content}
                    flowing
                    on="hover"
                    size="large"
                    position="right center"
                />
              </div>
          }
        </div>
    )
  };

  const renderPatientBirthDate = (data: any) => {
    const {cellData} = data;

    if (cellData) {
      return <div>{moment(cellData).format("DD.MM.YYYY")}</div>;
    }
    return "";
  }

  return (
      <VirtualizedTable
          rowCount={patients.length}
          rowGetter={patientsRowGetter}
          rowRenderer={patientsRowRenderer}
          columns={[
            {
              width: 50,
              flexGrow: 1,
              flexShrink: 0,
              label: selectHeaderColumn(),
              dataKey: "index",
              cellRenderer: patientSelectCellRenderer
            },
            {
              width: 200,
              flexGrow: 1,
              flexShrink: 0,
              label: (getMessage("patientEmail.table.header.email")),
              dataKey: "email"
            },
            {
              width: 50,
              flexGrow: 1,
              label: (getMessage("patientEmail.table.header.numberOfPatients")),
              dataKey: "numberOfPatients"
            },
            {
              width: 150,
              flexGrow: 2,
              label: (getMessage("patientEmail.table.header.patientIds")),
              dataKey: "patientIds"
            },
            {
              width: 50,
              flexGrow: 2,
              label: (getMessage("patientEmail.table.header.patientId1")),
              dataKey: "patientId1"
            },
            // {
            //   width: 100,
            //   label: (getMessage("patientEmail.table.header.patientCompanyId1")),
            //   dataKey: "patientCompanyId1"
            // },
            {
              width: 150,
              flexGrow: 2,
              label: (getMessage("patientEmail.table.header.patientCompany1")),
              dataKey: "patientCompany1"
            },
            {
              width: 150,
              flexGrow: 2,
              label: (getMessage("patientEmail.table.header.patientContainer1")),
              dataKey: "patientContainer1"
            },
            {
              width: 100,
              flexGrow: 2,
              label: (getMessage("patientEmail.table.header.patientBirthDate1")),
              dataKey: "patientBirthDate1",
              cellRenderer: renderPatientBirthDate
            },
            {
              width: 50,
              flexGrow: 2,
              label: (getMessage("patientEmail.table.header.patientGender1")),
              dataKey: "patientGender1"
            },
            // {
            //   width: 100,
            //   label: (getMessage("patientEmail.table.header.patientId2")),
            //   dataKey: "patientId2"
            // },
            // {
            //   width: 100,
            //   label: (getMessage("patientEmail.table.header.patientCompanyId2")),
            //   dataKey: "patientCompanyId2"
            // },
            // {
            //   width: 200,
            //   label: (getMessage("patientEmail.table.header.patientCompany2")),
            //   dataKey: "patientCompany2"
            // },
            // {
            //   width: 200,
            //   label: (getMessage("patientEmail.table.header.patientContainer2")),
            //   dataKey: "patientContainer2"
            // },
            // {
            //   width: 200,
            //   label: (getMessage("patientEmail.table.header.patientBirthDate2")),
            //   dataKey: "patientBirthDate2",
            //   cellRenderer: renderPatientBirthDate
            // },
            // {
            //   width: 50,
            //   label: (getMessage("patientEmail.table.header.patientGender2")),
            //   dataKey: "patientGender2"
            // },
            // {
            //   width: 100,
            //   label: (getMessage("patientEmail.table.header.patientId3")),
            //   dataKey: "patientId3"
            // },
            // {
            //   width: 100,
            //   label: (getMessage("patientEmail.table.header.patientCompanyId3")),
            //   dataKey: "patientCompanyId3"
            // },
            // {
            //   width: 200,
            //   label: (getMessage("patientEmail.table.header.patientCompany3")),
            //   dataKey: "patientCompany3"
            // },
            // {
            //   width: 200,
            //   label: (getMessage("patientEmail.table.header.patientContainer3")),
            //   dataKey: "patientContainer3"
            // },
            // {
            //   width: 200,
            //   label: (getMessage("patientEmail.table.header.patientBirthDate3")),
            //   dataKey: "patientBirthDate3",
            //   cellRenderer: renderPatientBirthDate
            // },
            // {
            //   width: 50,
            //   label: (getMessage("patientEmail.table.header.patientGender3")),
            //   dataKey: "patientGender3"
            // },
            // {
            //   width: 100,
            //   label: (getMessage("patientEmail.table.header.patientId4")),
            //   dataKey: "patientId4"
            // },
            // {
            //   width: 100,
            //   label: (getMessage("patientEmail.table.header.patientCompanyId4")),
            //   dataKey: "patientCompanyId4"
            // },
            // {
            //   width: 200,
            //   label: (getMessage("patientEmail.table.header.patientCompany4")),
            //   dataKey: "patientCompany4"
            //
            // },
            // {
            //   width: 200,
            //   label: (getMessage("patientEmail.table.header.patientContainer4")),
            //   dataKey: "patientContainer4"
            // },
            // {
            //   width: 200,
            //   label: (getMessage("patientEmail.table.header.patientBirthDate4")),
            //   dataKey: "patientBirthDate4",
            //   cellRenderer: renderPatientBirthDate
            // },
            // {
            //   width: 50,
            //   label: (getMessage("patientEmail.table.header.patientGender4")),
            //   dataKey: "patientGender4"
            // },
            // {
            //   width: 100,
            //   label: (getMessage("patientEmail.table.header.patientId5")),
            //   dataKey: "patientId5"
            // },
            // {
            //   width: 100,
            //   label: (getMessage("patientEmail.table.header.patientCompanyId5")),
            //   dataKey: "patientCompanyId5"
            // },
            // {
            //   width: 200,
            //   label: (getMessage("patientEmail.table.header.patientCompany5")),
            //   dataKey: "patientCompany5"
            // },
            // {
            //   width: 200,
            //   label: (getMessage("patientEmail.table.header.patientContainer5")),
            //   dataKey: "patientContainer5"
            // },
            // {
            //   width: 200,
            //   label: (getMessage("patientEmail.table.header.patientBirthDate5")),
            //   dataKey: "patientBirthDate5",
            //   cellRenderer: renderPatientBirthDate
            // },
            // {
            //   width: 50,
            //   label: (getMessage("patientEmail.table.header.patientGender5")),
            //   dataKey: "patientGender5"
            // }
          ]}
      />
  );

}

export default withMessages(withGuarantorsContext(PatientEmailImportTableView));
